import { FieldProvenanceType } from '../schemas/report.schemas';

/**
 * Turns the flat provenance lists the reports arrive with into a lookup addressed the way the tables
 * address it: a value cell knows only which report and which descriptor it renders, never the report
 * object, so scanning the list per cell is neither possible nor affordable at a few thousand cells.
 */

type ProvenanceCarrierType = {
    id: number | string;
    provenance?: FieldProvenanceType[];
};

export type ProvenanceLookupType = (
    reportId?: number | null,
    descriptorId?: string | null,
) => FieldProvenanceType[] | undefined;

/**
 * For tables with Source view switched off: nothing will ask it anything, so there is no reason to
 * index, but the callers still need a lookup of stable identity to hand down.
 */
export const EMPTY_PROVENANCE_LOOKUP: ProvenanceLookupType = () => undefined;

export function buildProvenanceLookup(reports: ProvenanceCarrierType[]): ProvenanceLookupType {
    const byReportId: Record<number, Record<string, FieldProvenanceType[]>> = {};

    for (const report of reports) {
        // Calculated columns carry a "TMP:n" id and the unsaved column carries "new"; neither is a
        // persisted report and neither can have provenance.
        if (!Number.isInteger(report?.id) || !Array.isArray(report.provenance)) {
            continue;
        }

        // An array per descriptor: one value can be grounded in several places — across two pages,
        // or in two different documents.
        const byDescriptorId: Record<string, FieldProvenanceType[]> = {};
        for (const entry of report.provenance) {
            if (!byDescriptorId[entry.descriptorId]) {
                byDescriptorId[entry.descriptorId] = [];
            }
            byDescriptorId[entry.descriptorId].push(entry);
        }

        byReportId[report.id as number] = byDescriptorId;
    }

    return (reportId, descriptorId) => {
        if (reportId === null || reportId === undefined || !descriptorId) {
            return undefined;
        }
        return byReportId[reportId]?.[descriptorId];
    };
}
